import { useEffect, useId, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Copy,
  Info,
  Loader,
  Lock,
  ShieldCheck,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { track } from "@/lib/analytics";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import { cn } from "@/lib/utils";
import type { AssetConfig, AssetSlug } from "@/lib/villagepando-assets";
import { useWallet, type TokenSym } from "@/hooks/use-wallet";

const SLUG_TO_TOKEN: Record<AssetSlug, TokenSym> = {
  solar: "SOLAR-PANDO",
  planty: "PLANTY-PANDO",
  aqua: "AQUA-PANDO",
};

type PayAsset = "USDC" | "SOL";

function fmtUsd(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function shortAddr(a: string) {
  return `${a.slice(0, 4)}…${a.slice(-4)}`;
}

export function AssetContributionPanel({ asset }: { asset: AssetConfig }) {
  const Icon = asset.icon;
  const globalWallet = useWallet();
  const wallet = globalWallet.address;
  const setWallet = (_addr: string | null) => {
    // Wallet lifecycle is owned by the global provider — keep the local
    // legacy callers as no-ops so the modal open/close flow still works.
  };
  const [pay, setPay] = useState<PayAsset>("USDC");
  const [amount, setAmount] = useState<string>("25");
  const [walletOpen, setWalletOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Derived: USD value of the input and token return at the milestone reference price.
  const numericAmount = Number(amount) || 0;
  const usdValue = pay === "USDC" ? numericAmount : numericAmount * asset.solUsdRef;
  const tokenReturn = usdValue / asset.refUsdPerToken;

  const pct = Math.min(
    100,
    Math.round((asset.milestone.raisedUsd / asset.milestone.goalUsd) * 100),
  );

  const copyTreasury = async () => {
    try {
      await navigator.clipboard.writeText(asset.treasury);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      track("cta_click", { id: "vp_copy_treasury", asset: asset.slug });
    } catch {
      /* ignored */
    }
  };

  const onContribute = () => {
    if (!wallet) {
      globalWallet.openConnect();
      return;
    }
    if (numericAmount <= 0) return;
    setConfirmOpen(true);
    track("cta_click", {
      id: "vp_contribute",
      asset: asset.slug,
      pay,
      amount: numericAmount,
      usd: Math.round(usdValue),
    });
  };

  // Called by ConfirmTransactionModal when the user approves the swap —
  // wires the mock contribution into the global multi-token wallet so the
  // RWA dashboard, EcosystemSwap, and every other consumer see it live.
  const onApproved = () => {
    globalWallet.debit(pay, numericAmount);
    globalWallet.credit(SLUG_TO_TOKEN[asset.slug], tokenReturn);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      {/* ───────────── Left: Milestone + real-world inventory ───────────── */}
      <section
        className="relative overflow-hidden rounded-3xl border bg-white/[0.015] p-6 sm:p-8"
        style={{ borderColor: `${asset.color.replace(")", " / 0.28)")}` }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full blur-3xl opacity-40"
          style={{ background: asset.glow }}
        />

        <header className="relative z-10 flex items-start justify-between gap-3">
          <div>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{
                borderColor: `${asset.color.replace(")", " / 0.4)")}`,
                color: asset.color,
                background: `${asset.color.replace(")", " / 0.06)")}`,
              }}
            >
              <Icon className="h-3 w-3" />
              {asset.milestone.phase}
            </span>
            <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">
              {asset.milestone.title}
            </h3>
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
              {asset.milestone.body}
            </p>
          </div>
        </header>

        {/* Progression */}
        <div className="relative z-10 mt-6 rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Funded
              </div>
              <div className="mt-1 font-display text-2xl font-semibold tracking-tight">
                {fmtUsd(asset.milestone.raisedUsd)}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  / {fmtUsd(asset.milestone.goalUsd)}
                </span>
              </div>
            </div>
            <div
              className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: asset.color }}
            >
              {pct}%
            </div>
          </div>
          <div
            className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${asset.name} milestone funding progress`}
          >
            <div
              className="h-full rounded-full transition-[width] duration-700"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${asset.color}, oklch(0.95 0.08 95))`,
                boxShadow: `0 0 18px ${asset.color.replace(")", " / 0.55)")}`,
              }}
            />
          </div>
        </div>

        {/* Real-world inventory barometer */}
        <div className="relative z-10 mt-6">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <Zap className="h-3 w-3" style={{ color: asset.color }} />
            Real-world inventory barometer
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {asset.milestone.metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
              >
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                  {m.label}
                </div>
                <div className="mt-1 font-display text-xl font-semibold tracking-tight">
                  {m.value}
                </div>
                {m.sub && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{m.sub}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Right: Contribution engine ───────────── */}
      <section
        className="relative overflow-hidden rounded-3xl border bg-white/[0.015] p-6 sm:p-8"
        style={{ borderColor: `${asset.color.replace(")", " / 0.28)")}` }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl opacity-40"
          style={{ background: asset.glow }}
        />

        <div className="relative z-10 flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              // Direct Treasury Swap
            </div>
            <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight">
              Contribute & mint{" "}
              <span style={{ color: asset.color }}>{asset.symbol}</span>
            </h3>
          </div>
          <ConnectWalletButton
            wallet={wallet}
            color={asset.color}
            onClick={() => setWalletOpen(true)}
          />
        </div>

        {/* Pay-asset toggle */}
        <div className="relative z-10 mt-6 inline-flex rounded-full border border-white/10 bg-black/30 p-1">
          {(["USDC", "SOL"] as PayAsset[]).map((a) => {
            const active = pay === a;
            return (
              <button
                key={a}
                type="button"
                onClick={() => setPay(a)}
                className={cn(
                  "rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors",
                  active ? "text-obsidian" : "text-muted-foreground hover:text-foreground",
                )}
                style={
                  active
                    ? { background: asset.color }
                    : undefined
                }
                aria-pressed={active}
              >
                {a}
              </button>
            );
          })}
        </div>

        {/* Amount input */}
        <div className="relative z-10 mt-4 rounded-2xl border border-white/10 bg-black/30 p-4">
          <label
            htmlFor={`vp-amount-${asset.slug}`}
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
          >
            You deposit
          </label>
          <div className="mt-2 flex items-baseline gap-3">
            <input
              id={`vp-amount-${asset.slug}`}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              className="w-full bg-transparent font-display text-4xl font-semibold tracking-tight outline-none placeholder:text-muted-foreground/40"
              placeholder="0.00"
            />
            <span className="font-mono text-sm text-muted-foreground">{pay}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            ≈ {fmtUsd(usdValue)} USD value
          </div>
        </div>

        {/* Arrow */}
        <div className="relative z-10 my-2 flex justify-center">
          <div
            className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-black/40"
            style={{ color: asset.color }}
          >
            <ArrowRight className="h-4 w-4 -rotate-90" />
          </div>
        </div>

        {/* Token return */}
        <div
          className="relative z-10 rounded-2xl border bg-black/30 p-4"
          style={{ borderColor: `${asset.color.replace(")", " / 0.4)")}` }}
        >
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              You receive
            </div>
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em]"
              style={{ background: asset.colorSoft, color: asset.color }}
            >
              <Lock className="h-3 w-3" />
              fixed reference
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="font-display text-4xl font-semibold tracking-tight">
              {tokenReturn.toLocaleString("en-US", {
                maximumFractionDigits: 2,
              })}
            </div>
            <span
              className="font-mono text-sm font-semibold"
              style={{ color: asset.color }}
            >
              {asset.symbol}
            </span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            1 {asset.symbol} = {fmtUsd(asset.refUsdPerToken)} · milestone-locked
          </div>
        </div>

        {/* Treasury readout */}
        <div className="relative z-10 mt-5 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
              Project treasury (hardcoded)
            </div>
            <div className="mt-0.5 truncate font-mono text-xs text-foreground/80" title={asset.treasury}>
              {shortAddr(asset.treasury)}
            </div>
          </div>
          <button
            type="button"
            onClick={copyTreasury}
            aria-label="Copy treasury address"
            className="inline-flex h-8 items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
          >
            {copied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        {/* Contribute CTA */}
        <button
          type="button"
          onClick={onContribute}
          disabled={numericAmount <= 0}
          className="relative z-10 mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition-transform duration-200 hover:scale-[1.01] disabled:opacity-50"
          style={{
            background: `linear-gradient(135deg, ${asset.color}, oklch(0.95 0.08 95))`,
            boxShadow: `0 12px 40px -10px ${asset.color.replace(")", " / 0.55)")}`,
          }}
        >
          {wallet ? "Confirm contribution" : "Connect wallet to contribute"}
          <ArrowRight className="h-4 w-4" />
        </button>

        {/* Security label */}
        <div className="relative z-10 mt-4 flex gap-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: asset.color }} />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            This project utilises <span className="text-foreground/90">milestone-based funding</span>{" "}
            to lock in real-world infrastructure assets and suppress speculative market inflation.
            Token distribution is fully asset-backed. Contributions route directly to the project
            treasury — not an open DEX pool.
          </p>
        </div>

        <p className="relative z-10 mt-2 flex items-start gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/80">
          <Info className="mt-px h-3 w-3 shrink-0" />
          Simulation interface · live mint activates on milestone verification.
        </p>
      </section>

      <ConnectWalletModal
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        color={asset.color}
        onConnected={(addr) => {
          setWallet(addr);
          setWalletOpen(false);
          track("cta_click", { id: "vp_wallet_connect", asset: asset.slug });
        }}
      />

      <ConfirmTransactionModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onApproved={onApproved}
        asset={asset}
        pay={pay}
        amount={numericAmount}
        usd={usdValue}
        tokens={tokenReturn}
        wallet={wallet}
      />
    </div>
  );
}

/* ─────────────────────────── Connect Wallet ─────────────────────────── */

function ConnectWalletButton({
  wallet,
  color,
  onClick,
}: {
  wallet: string | null;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-2 rounded-full border bg-white/[0.04] px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/85 transition-colors hover:bg-white/[0.08]"
      style={{ borderColor: `${color.replace(")", " / 0.4)")}` }}
    >
      <Wallet className="h-3.5 w-3.5" style={{ color }} />
      {wallet ? shortAddr(wallet) : "Connect wallet"}
    </button>
  );
}

function ConnectWalletModal({
  open,
  onClose,
  onConnected,
  color,
}: {
  open: boolean;
  onClose: () => void;
  onConnected: (addr: string) => void;
  color: string;
}) {
  const ref = useModalA11y({ open, onClose, analyticsId: "vp_wallet" });
  const titleId = useId();
  const [pending, setPending] = useState<null | "phantom" | "solflare" | "backpack">(null);

  useEffect(() => {
    if (!open) setPending(null);
  }, [open]);

  if (!open) return null;

  const fakeConnect = (id: "phantom" | "solflare" | "backpack") => {
    setPending(id);
    setTimeout(() => {
      // Deterministic-ish demo address per provider (purely UI sim, never on-chain).
      const seed = id === "phantom" ? "PhAn" : id === "solflare" ? "SolF" : "BkPk";
      const tail = Math.random().toString(36).slice(2, 6).toUpperCase();
      onConnected(`${seed}wAlLeTdEmO${tail}11111111111111111111111`);
    }, 900);
  };

  return (
    <div
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-6"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 id={titleId} className="font-display text-xl font-semibold tracking-tight">
          Connect a Solana wallet
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Approval is simulated locally — no transaction is broadcast.
        </p>
        <div className="mt-5 space-y-2">
          {(
            [
              { id: "phantom", label: "Phantom" },
              { id: "solflare", label: "Solflare" },
              { id: "backpack", label: "Backpack" },
            ] as const
          ).map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => fakeConnect(w.id)}
              disabled={!!pending}
              className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium transition-colors hover:bg-white/[0.06] disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <Wallet className="h-4 w-4" style={{ color }} />
                {w.label}
              </span>
              {pending === w.id ? (
                <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ))}
        </div>
        <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
          We never read wallet balances or token accounts. Privacy-hardened RPC.
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────── Confirm Tx ─────────────────────────── */

function ConfirmTransactionModal({
  open,
  onClose,
  onApproved,
  asset,
  pay,
  amount,
  usd,
  tokens,
  wallet,
}: {
  open: boolean;
  onClose: () => void;
  onApproved?: () => void;
  asset: AssetConfig;
  pay: PayAsset;
  amount: number;
  usd: number;
  tokens: number;
  wallet: string | null;
}) {
  const ref = useModalA11y({ open, onClose, analyticsId: `vp_confirm_${asset.slug}` });
  const titleId = useId();
  const [state, setState] = useState<"review" | "approving" | "done">("review");

  useEffect(() => {
    if (open) setState("review");
  }, [open]);

  if (!open) return null;

  const approve = () => {
    setState("approving");
    setTimeout(() => {
      setState("done");
      onApproved?.();
    }, 1300);
    track("cta_click", { id: "vp_confirm_approve", asset: asset.slug, usd: Math.round(usd) });
  };


  return (
    <div
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.10_0.012_260_/_0.97)] p-6"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl opacity-50"
          style={{ background: asset.glow }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative z-10">
          <h3 id={titleId} className="font-display text-xl font-semibold tracking-tight">
            {state === "done" ? "Contribution recorded" : `Review ${asset.symbol} contribution`}
          </h3>

          {state !== "done" ? (
            <>
              <div className="mt-4 space-y-2 rounded-2xl border border-white/10 bg-black/30 p-4 text-sm">
                <Row label="From wallet" value={wallet ? shortAddr(wallet) : "—"} mono />
                <Row label="Deposit" value={`${amount} ${pay}`} />
                <Row label="USD value" value={fmtUsd(usd)} />
                <Row label="Reference price" value={`${fmtUsd(asset.refUsdPerToken)} / ${asset.symbol}`} />
                <Row
                  label="You receive"
                  value={`${tokens.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${asset.symbol}`}
                  highlight={asset.color}
                />
                <Row label="Routed to" value={shortAddr(asset.treasury)} mono />
              </div>
              <button
                type="button"
                onClick={approve}
                disabled={state === "approving"}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian disabled:opacity-60"
                style={{
                  background: `linear-gradient(135deg, ${asset.color}, oklch(0.95 0.08 95))`,
                }}
              >
                {state === "approving" ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Awaiting wallet approval…
                  </>
                ) : (
                  "Approve in wallet"
                )}
              </button>
              <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                Simulation only · no on-chain transaction is broadcast.
              </p>
            </>
          ) : (
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0" style={{ color: asset.color }} />
                <div>
                  <div className="font-display text-lg font-semibold">
                    {tokens.toLocaleString("en-US", { maximumFractionDigits: 2 })} {asset.symbol} reserved
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Funds are queued to the project treasury and will mint asset-backed tokens
                    once the milestone clears verification.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-white/[0.08]"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <span
        className={cn("text-sm font-medium", mono && "font-mono text-xs")}
        style={highlight ? { color: highlight } : undefined}
      >
        {value}
      </span>
    </div>
  );
}
